import React, {
  HTMLInputTypeAttribute,
  useLayoutEffect,
  useRef,
  useEffect,
  useState,
} from "react";
import { Toaster } from "react-hot-toast";
import { Z_INDEXES } from "./constants";

// Dropdown component with click-outside-to-close behavior
export function Dropdown({
  trigger,
  children,
  open,
  onOpenChange,
  align = "right",
  className = "",
}: {
  trigger: React.ReactNode;
  children: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  align?: "left" | "right";
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onOpenChange(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onOpenChange]);

  return (
    <div className="relative" ref={containerRef}>
      <div onClick={() => onOpenChange(!open)}>{trigger}</div>
      {open && (
        <div
          className={`absolute top-full mt-1 border border-border rounded-lg shadow-lg ${
            Z_INDEXES.DROPDOWN
          } ${align === "right" ? "right-0" : "left-0"} ${className}`}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export function Button(props: {
  children: React.ReactNode;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  smaller?: boolean;
  textClass?: string;
  fullWidth?: boolean;
  inert?: boolean;
}) {
  return (
    <button
      className={`${
        props.smaller ? "w-28" : "w-40"
      } h-12 p-4 bg-bg-brand text-text-on-brand rounded border-2 border-border flex items-center justify-center ${
        props.textClass
      } ${props.fullWidth ? "w-full" : ""} ${props.inert ? "cursor-default" : ""}`}
      onClick={(e) => props.onClick(e)}
    >
      {props.children}
    </button>
  );
}

export function Header(props: {
  right?: React.ReactNode;
  subtitle?: React.ReactNode;
}) {
  return (
    <div className="relative flex flex-row py-8 w-full px-2 sm:px-0 items-start justify-between text-text">
      <div className="flex flex-col items-start gap-1">
        <h1 className="text-3xl">skeetbowl!</h1>
        {props.subtitle}
      </div>
      {props.right}
    </div>
  );
}

export function Input({
  value,
  placeholder,
  setValue,
  type,
}: {
  value: string;
  placeholder: string;
  setValue: (value: string) => void;
  type?: HTMLInputTypeAttribute;
}) {
  return (
    <input
      className="h-12 p-3 w-full text-lg rounded border-4 border-border font-light bg-inherit"
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    ></input>
  );
}

export function Select<T extends string>({
  value,
  setValue,
  options,
}: {
  value: T;
  setValue?: (value: T) => void;
  options: Array<{ value: T; label: string; disabled?: boolean }>;
}) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value);
  return (
    <Dropdown
      trigger={
        <button
          type="button"
          className="h-12 px-3 w-full text-lg rounded border-4 border-border font-light bg-bg-brand-alt text-text-on-brand-alt cursor-pointer flex items-center justify-between"
        >
          <span className="truncate">{current?.label ?? ""}</span>
          <span className="text-sm opacity-70">▾</span>
        </button>
      }
      open={open}
      onOpenChange={setOpen}
      align="left"
      className="w-full p-2 bg-bg-brand-alt text-text-on-brand-alt text-xl"
    >
      {options.map((o) => (
        <div key={o.value}>
          <button
            type="button"
            disabled={o.disabled}
            className={
              o.disabled
                ? "w-full text-left p-2 rounded opacity-40 line-through cursor-not-allowed"
                : "w-full text-left p-2 rounded cursor-pointer hover:bg-black/10"
            }
            onClick={() => {
              if (o.disabled) return;
              setValue?.(o.value);
              setOpen(false);
            }}
          >
            {o.label}
          </button>
        </div>
      ))}
    </Dropdown>
  );
}

export function Section({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative w-full p-4 rounded border-1 bg-bg-surface flex-col items-center inline-flex">
      {children}
    </div>
  );
}

export function Page({ children }: { children: React.ReactNode }) {
  return (
    <div
      id="page"
      className="select-none flex flex-col items-center font-light text-text"
    >
      <div className="w-full max-w-lg">{children}</div>
      <Toaster />
    </div>
  );
}

export function Modal({
  children,
  width = "fit-content",
}: {
  children: React.ReactNode;
  width?: "sm" | "md" | "lg" | "fit-content";
}) {
  useLayoutEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  return (
    <div
      className={`fixed h-dvh w-screen top-0 left-0 flex flex-col justify-center items-center ${Z_INDEXES.MODAL_BACKGROUND}`}
    >
      <div
        className={`absolute h-full w-full bg-bg-modal-backdrop opacity-80`}
      />
      <div
        className={`h-full w-full flex flex-col justify-center items-center ${Z_INDEXES.MODAL}`}
      >
        <div
          className={`rounded-xl bg-bg-surface flex flex-col p-8 max-w-lg my-8 overflow-y-auto ${
            width === "sm"
              ? "w-1/2"
              : width === "md"
                ? "w-3/4"
                : width === "lg"
                  ? "w-4/5"
                  : ""
          }`}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
